<template>
  <div class="page-content">

    <!-- Error Message -->
    <v-alert v-if="error" type="error" dismissible @update:modelValue="error = null">
      {{ error }}
    </v-alert>

    <!-- Tabs -->
    <v-card class="mx-auto elevation-2 tabs-card" style="margin-top: 20px;">
      <v-container fluid>
        <v-row align="center" justify="space-between">
          <v-tabs v-model="activeTab" class="dashboard-tab" show-arrows next-icon="mdi-arrow-right-bold-box-outline" prev-icon="mdi-arrow-left-bold-box-outline">
            <template v-if="isSatelliteMonitor">
              <v-tab value="satellite">Satellite</v-tab>
            </template>
            <template v-else>
              <v-tab value="studio">Studio</v-tab>
              <v-tab v-for="tab in tabs" :key="tab.value" :value="tab.value">{{ tab.label }}</v-tab>
            </template>
          </v-tabs>
        </v-row>
      </v-container>
    </v-card>

    <!-- Cards Row: Input Cards -->
    <v-row style="margin-top: 10px;">
      <!-- 1st Card: Daily Sales -->
      <v-col cols="12" md="3">
        <v-card :class="['elevation-2', activeCard === 'dailySales' ? 'active-card' : '']" @click="setActiveCard('dailySales')" style="min-height: 180px; border-radius: 6px;">
          <v-card-text style="padding: 8px 16px; text-align: center;">
            <div v-if="dailySalesBreakdown.total > 0" class="headline d-flex justify-center align-center">
              $<span class="text-h6">{{ dailySalesBreakdown.total.toFixed(2) }}</span>
            </div>
            <div v-else style="font-size: 12px; color: #666;">
              {{ dailySalesBreakdown.total === 0 ? `No sales data for ${formatDate(dailySalesDate)}` : 'Loading sales data...' }}
            </div>
          </v-card-text>
          <v-card-title style="position: relative; display: flex; align-items: center; padding: 8px 16px;">
            <v-icon style="margin-right: 8px; font-size: 28px;">mdi-calendar</v-icon>
            <span style="position: absolute; left: 50%; transform: translateX(-50%); font-size: 16px; font-weight: 600;">Daily Sales</span>
          </v-card-title>
          <v-card-text style="padding: 4px 16px;">
            <v-date-input
              ref="dailyDatePicker"
              v-model="dailySalesDate"
              label="Select Date"
              prepend-icon=""
              :hide-actions="false"
              placeholder="yyyy/mm/dd"
              :clearable="false"
              @update:modelValue="val => { dailySalesDate = val; fetchDailySalesAndModel(val); setActiveCard('dailySales'); }"
            ></v-date-input>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- 2nd Card: Select Date Range -->
      <v-col cols="12" md="3">
        <v-card :class="['elevation-2', activeCard === 'dateRange' ? 'active-card' : '']" @click="setActiveCard('dateRange')" style="min-height: 180px; border-radius: 6px;">
          <v-card-text style="padding: 8px 16px; text-align: center;">
            <div v-if="rangeSales !== null && rangeSales > 0" class="headline d-flex justify-center align-center">
              $<span class="text-h6">{{ rangeSales.toFixed(2) }}</span>
            </div>
            <div v-else style="font-size: 12px; color: #666;">Select a date range to view sales</div>
          </v-card-text>
          <v-card-title style="position: relative; display: flex; align-items: center; padding: 8px 16px;">
            <v-icon style="margin-right: 8px; font-size: 28px;">mdi-calendar-multiple</v-icon>
            <span style="position: absolute; left: 50%; transform: translateX(-50%); font-size: 16px; font-weight: 600;">Select Date Range</span>
          </v-card-title>
          <v-card-text style="padding: 4px 16px;">
            <v-date-input
              v-model="dateSelectRange"
              label="Select Range"
              prepend-icon=""
              max-width="368"
              :multiple="'range'"
              @update:modelValue="(val) => { handleDateRangeSelection(val); setActiveCard('dateRange'); }"
              ref="datePicker"
              :clearable="false"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- 3rd Card: Select Period -->
      <v-col cols="12" md="3">
        <v-card :class="['elevation-2', activeCard === 'selectPeriod' ? 'active-card' : '']" @click="setActiveCard('selectPeriod')" style="min-height: 180px; border-radius: 6px;">
          <v-card-text style="padding: 8px 16px; text-align: center;">
            <div v-if="periodSales !== null && periodSales > 0" class="headline d-flex justify-center align-center">
              $<span class="text-h6">{{ periodSales.toFixed(2) }}</span>
            </div>
            <div v-else style="font-size: 12px; color: #666;">Select a period to view sales</div>
          </v-card-text>
          <v-card-title style="position: relative; display: flex; align-items: center; padding: 8px 16px;">
            <v-icon style="margin-right: 8px; font-size: 28px;">mdi-calendar-multiple-check</v-icon>
            <span style="position: absolute; left: 50%; transform: translateX(-50%); font-size: 16px; font-weight: 600;">Select Period</span>
          </v-card-title>
          <v-card-text style="padding: 4px 16px;">
            <v-select
              v-model="selectedPeriod"
              :items="periodOptions"
              item-title="text"
              item-value="value"
              label="Select Period"
              outlined
              :disabled="loadingPeriods || periodOptions.length === 0 || periodOptions[0]?.value === null"
              @update:modelValue="(val) => { fetchPeriodSales(val); setActiveCard('selectPeriod'); }"
            ></v-select>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- 4th Card: View Sites -->
      <v-col cols="12" md="3">
        <v-card :class="['elevation-2', activeCard === 'viewSites' ? 'active-card' : '']" @click="setActiveCard('viewSites')" style="min-height: 180px; border-radius: 6px;">
          <v-card-title style="position: relative; display: flex; align-items: center; padding: 8px 16px;">
            <v-icon style="margin-right: 8px; font-size: 28px;">mdi-web</v-icon>
            <span style="position: absolute; left: 50%; transform: translateX(-50%); font-size: 16px; font-weight: 600;">View Sites</span>
          </v-card-title>
          <v-card-text style="padding: 4px 16px;">
            <v-row dense>
              <v-col v-for="site in enabledSites" :key="site.name" cols="6">
                <v-chip
                  :color="siteColors[site.name]"
                  text-color="white"
                  small
                  style="width: 100%; justify-content: center; font-weight: bold;"
                >
                  {{ site.name }}
                </v-chip>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dashboard Content -->
    <template v-if="models.length !== 0">
      <!-- Charts Row: Donut Charts -->
      <v-row class="mt-3">
        <v-col cols="12" md="4">
          <div style="font-size: 14px; text-align: center; padding: 8px 0 8px 0;">Daily Goal ${{ salesTarget.toFixed(2) }}</div>
          <DonutChart :total-sales="totalSales" :sales-target="salesTarget" :height="260" />
        </v-col>
        <v-col cols="12" md="4">
          <div style="font-size: 14px; text-align: center; padding: 8px 0 8px 0;">Range Goal</div>
          <DonutChart :total-sales="rangeSales" :sales-target="rangeSalesTarget" :height="260" />
        </v-col>
        <v-col cols="12" md="4">
          <div style="font-size: 14px; text-align: center; padding: 8px 0 8px 0;">
            Platform Sales
            <span v-if="activeCard === 'dailySales'">(Daily)</span>
            <span v-else-if="activeCard === 'dateRange'">(Range)</span>
            <span v-else-if="activeCard === 'selectPeriod'">(Period)</span>
          </div>
          <DonutChartWebsites :platform-sales="filteredPlatformSales" :height="260" />
        </v-col>
      </v-row>

      <!-- Charts Row: Bar Chart -->
      <v-row class="mt-3">
        <v-col cols="12" md="12">
          <v-card class="elevation-0.5 pa-2 compact-card">
            <v-card-title style="font-size: 14px; text-align: center; padding: 8px 0;">Daily Sales Over Time</v-card-title>
            <v-card-text style="padding: 0;">
              <Barchart :daily-sales="chartDailySales" height="150" />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Cards Row: Top Performers -->
      <v-row style="margin-top: 10px;">
        <v-col cols="12" md="3">
          <v-card class="elevation-2" style="min-height: 90px; border-radius: 6px;">
            <v-card-title style="position: relative; display: flex; align-items: center; padding: 8px 16px;">
              <v-icon style="margin-right: 8px; font-size: 20px;">mdi-crown</v-icon>
              <span style="position: absolute; left: 50%; transform: translateX(-50%); font-size: 14px; font-weight: 600;">Model of the Day</span>
            </v-card-title>
            <v-card-text style="padding: 4px 16px; text-align: center;">
              <span v-if="modelOfTheDay" style="font-size: 16px; font-weight: 500;">{{ modelOfTheDay.stageName || modelOfTheDay.fullName || 'Unknown' }}</span>
              <span v-else style="font-size: 12px; color: #666;">No data for {{ formatDate(dailySalesDate) }}</span>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card class="elevation-2" style="min-height: 90px; border-radius: 6px;">
            <v-card-title style="position: relative; display: flex; align-items: center; padding: 8px 16px;">
              <v-icon style="margin-right: 8px; font-size: 20px;">mdi-crown</v-icon>
              <span style="position: absolute; left: 50%; transform: translateX(-50%); font-size: 14px; font-weight: 600;">Top Model (Range)</span>
            </v-card-title>
            <v-card-text style="padding: 4px 16px; text-align: center;">
              <span v-if="topModelRange" style="font-size: 16px; font-weight: 500;">{{ topModelRange.stageName || topModelRange.fullName || 'Unknown' }}</span>
              <span v-else style="font-size: 12px; color: #666;">No data for range</span>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card class="elevation-2" style="min-height: 90px; border-radius: 6px;">
            <v-card-title style="position: relative; display: flex; align-items: center; padding: 8px 16px;">
              <v-icon style="margin-right: 8px; font-size: 20px;">mdi-crown</v-icon>
              <span style="position: absolute; left: 50%; transform: translateX(-50%); font-size: 14px; font-weight: 600;">Model of the Period</span>
            </v-card-title>
            <v-card-text style="padding: 4px 16px; text-align: center;">
              <span v-if="modelOfThePeriod" style="font-size: 16px; font-weight: 500;">{{ modelOfThePeriod.stageName || modelOfThePeriod.fullName || 'Unknown' }}</span>
              <span v-else style="font-size: 12px; color: #666;">No data for period</span>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card class="elevation-2" style="min-height: 90px; border-radius: 6px;">
            <v-card-title style="position: relative; display: flex; align-items: center; padding: 8px 16px;">
              <v-icon style="margin-right: 8px; font-size: 20px;">mdi-web</v-icon>
              <span style="position: absolute; left: 50%; transform: translateX(-50%); font-size: 14px; font-weight: 600;">
                {{ activeCard === 'dailySales' ? 'Top Site (Daily)' : activeCard === 'dateRange' ? 'Top Site (Range)' : activeCard === 'selectPeriod' ? 'Top Site (Period)' : 'Top Site' }}
              </span>
            </v-card-title>
            <v-card-text style="padding: 4px 16px; text-align: center;">
              <span v-if="topSite" style="font-size: 16px; font-weight: 500;">{{ topSite.name }}</span>
              <span v-else style="font-size: 12px; color: #666;">No site data</span>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </div>
</template>





<script>
import secureAxios from '@/utils/secureAxios';
import Barchart from '@/components/BarChart.vue';
import DonutChartWebsites from '@/components/DonutChartWebsites.vue';
import DonutChart from '@/components/DonutChart.vue';

export default {
  name: 'Dashboard',
  components: {
    Barchart,
    DonutChartWebsites,
    DonutChart
  },
  data() {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1); // Yesterday: April 6, 2025
    const firstOfMonth = new Date(yesterday);
    firstOfMonth.setDate(1); // First of current month: April 1, 2025

    return {
      rangeSalesBreakdown: {},
      periodSalesBreakdown: {},
      activeTab: 'studio', // will be overridden for satellite monitors in mounted()
      activeCard: 'dateRange', // Default to dateRange
      tabs: [
        { value: 'morning', label: 'Morning' },
        { value: 'evening', label: 'Evening' },
        { value: 'night', label: 'Night' },
        { value: 'satellite', label: 'Satellite' },
        { value: 'active', label: 'Active' },
      ],
      dailySalesDate: yesterday,
      dateSelectRange: [firstOfMonth, yesterday],
      periodOptions: [],
      selectedPeriod: null,
      models: [],
      modelOfTheDay: null,
      topModelRange: null,
      modelOfThePeriod: null,
      topSite: null,
      sites: [
        { key: 'flirt4free', name: 'Flirt4Free', enabled: true },
        { key: 'streamate', name: 'Streamate', enabled: true },
        { key: 'livejasmin', name: 'LiveJasmin', enabled: true },
        { key: 'chaturbate', name: 'Chaturbate', enabled: true },
        { key: 'stripchat', name: 'StripChat', enabled: true },
        { key: 'kwiky', name: 'Kwiky', enabled: false },
        { key: 'sinparty', name: 'Sin Party', enabled: false },
        { key: 'cams', name: 'Cams', enabled: false },
        { key: 'cam4', name: 'Cam4', enabled: false },
        { key: 'bonga', name: 'Bonga', enabled: false }
      ],
      siteColors: {
        'Flirt4Free': 'rgba(2, 168, 224, 0.5)',
        'Streamate': 'rgba(35, 101, 159, 0.5)',
        'LiveJasmin': 'rgba(188, 0, 2, 0.5)',
        'Chaturbate': 'rgba(255, 203, 74, 0.8)',
        'Stripchat': 'rgba(0, 128, 0, 0.5)',
        'StripChat': 'rgba(0, 128, 0, 0.5)',
        'Kwiky': 'rgba(142, 68, 173, 0.5)',
        'Sin Party': 'rgba(217, 107, 141, 0.6)',
        'Cams': 'rgba(0, 166, 166, 0.6)',
        'Cam4': 'rgba(240, 138, 36, 0.6)',
        'Bonga': 'rgba(115, 86, 168, 0.6)'
      },
      dailySales: null,
      dailySalesBreakdown: {
        flirt4free: 0,
        streamate: 0,
        livejasmin: 0,
        chaturbate: 0,
        stripchat: 0,
        kwiky: 0,
        sinparty: 0,
        cams: 0,
        cam4: 0,
        bonga: 0,
        xlove: 0,
        total: 0
      },
      rangeSales: null,
      periodSales: null,
      chartDailySales: [],
      platformSales: {
        flirt4free: 0,
        streamate: 0,
        livejasmin: 0,
        chaturbate: 0,
        stripchat: 0,
        kwiky: 0,
        sinparty: 0,
        cams: 0,
        cam4: 0,
        bonga: 0,
        xlove: 0
      },
      totalSales: 0,
      rangeSalesTarget: 0,
      salesTarget: 750,
      loadingPeriods: false,
      error: null // Add error state
    };
  },
  computed: {
    isSatelliteMonitor() {
      try {
        const raw = localStorage.getItem('user');
        const u = raw ? JSON.parse(raw) : null;
        return u?.role === 'satellite_monitor';
      } catch (e) {
        return false;
      }
    },
    selectedPeriodText() {
      const period = this.periodOptions.find(p => p.value === this.selectedPeriod);
      return period ? period.text : '';
    },
    enabledSites() {
      return this.sites.filter(site => site.enabled);
    },
    // filteredModels and selectedModel logic removed as filter is gone.
    filteredPlatformSales() {
      let source = this.platformSales;

      if (this.activeCard === 'dailySales') {
        source = this.dailySalesBreakdown;
      } else if (this.activeCard === 'dateRange') {
        source = this.rangeSalesBreakdown || {};
      } else if (this.activeCard === 'selectPeriod') {
        source = this.periodSalesBreakdown || {};
      }

      return Object.fromEntries([
        ...this.sites.map(site => [site.key, site.enabled || Number(source[site.key] || 0) !== 0 ? (source[site.key] || 0) : 0]),
        ['xlove', 0],
      ]);
    }
  },
  watch: {
    activeTab() {
      this.fetchAllData();
    },
    dailySalesDate(newVal, oldVal) {
      if (!newVal || !(newVal instanceof Date) || isNaN(newVal.getTime())) {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        this.dailySalesDate = yesterday;
        if (process.env.NODE_ENV !== 'production') console.warn('Invalid dailySalesDate, reset to yesterday:', this.formatDate(yesterday));
        this.fetchDailySalesAndModel(yesterday);
        return;
      }
      const newDateStr = this.formatDate(newVal);
      const oldDateStr = oldVal ? this.formatDate(oldVal) : null;
      if (newDateStr === oldDateStr) {
        return;
      }
      this.fetchDailySalesAndModel(newVal);
    },
    dateSelectRange(newRange) {
      if (newRange.length >= 2) {
        const sorted = [...newRange].map(d => new Date(d)).sort((a, b) => a - b);
        const start = sorted[0];
        const end = sorted[sorted.length - 1];

        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);

        const daysSelected = Math.floor((end - start) / 86400000) + 1;
        this.rangeSalesTarget = this.salesTarget * daysSelected;

        // Only update rangeSalesTarget, then fetch relevant data
        this.fetchRangeSales();
        this.fetchTopModelRange();
        if (this.activeCard === 'dateRange') {
          this.fetchTopSite();
        }
      }
    },
    selectedPeriod() {
      this.fetchModelOfThePeriod();
      this.fetchPeriodSales();
      if (this.activeCard === 'selectPeriod') this.fetchTopSite();
    },
    activeCard(newCard) {
      this.fetchTopSite();
    }
  },
  async mounted() {
    // Satellite monitor dashboard: only show Satellite tab and start there
    if (this.isSatelliteMonitor) {
      this.activeTab = 'satellite';
    }
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    this.dailySalesDate = yesterday; // Set to yesterday

    await this.fetchSiteConfiguration();

    // Fetch data for yesterday
    await this.fetchDailySalesAndModel(this.dailySalesDate);

    // Fetch other initial data
    await this.fetchPeriods();
    this.activeCard = 'dateRange';
    await this.fetchAllData();

    // Set rangeSalesTarget for Range Goal donut
    const sortedDates = [...this.dateSelectRange].map(d => new Date(d)).sort((a, b) => a - b);
    const start = sortedDates[0];
    const end = sortedDates[sortedDates.length - 1];
    const daysSelected = Math.floor((end - start) / 86400000) + 1;
    this.rangeSalesTarget = this.salesTarget * daysSelected;
  },
  methods: {
    async fetchSiteConfiguration() {
      try {
        const { data } = await secureAxios.get('/api/config/sites');
        const configured = new Map((data?.sites || []).map(site => [site.key, site]));
        this.sites = this.sites.map(site => ({
          ...site,
          enabled: configured.has(site.key) ? Boolean(configured.get(site.key).enabled) : site.enabled,
        }));
      } catch (error) {
        this.handleError(error, 'fetchSiteConfiguration');
      }
    },
    async initializeDashboard() {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      this.dailySalesDate = yesterday; // Set to yesterday

      // Simulate calendar selection
      this.$nextTick(async () => {
        if (this.$refs.dailyDatePicker) {
          this.dailySalesDate = new Date(yesterday); // Reassign to trigger reactivity
          await this.fetchDailySalesAndModel(this.dailySalesDate);
        }
      });

      await this.fetchPeriods();
      this.activeCard = 'dateRange';
      await this.fetchAllData();
      // Set rangeSalesTarget for Range Goal donut on page load
      const sortedDates = [...this.dateSelectRange].map(d => new Date(d)).sort((a, b) => a - b);
      const start = sortedDates[0];
      const end = sortedDates[sortedDates.length - 1];
      const daysSelected = Math.floor((end - start) / 86400000) + 1;
      this.rangeSalesTarget = this.salesTarget * daysSelected;
    },
    async fetchAllData() {
      await Promise.all([
        this.fetchModels(),
        this.fetchRangeSales(),
        this.fetchPeriodSales(),
        this.fetchTopModelRange(),
        this.fetchModelOfThePeriod(),
        this.fetchTopSite()
      ]);
    },
    async fetchDailySalesAndModel(date) {
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        date = yesterday;
        this.dailySalesDate = yesterday; // Update reactive property
        if (process.env.NODE_ENV !== 'production') console.warn('Invalid date, defaulting to yesterday:', this.formatDate(date));
      }
      const dateStr = this.formatDate(date);
      try {
        await Promise.all([
          this.fetchDailySales(dateStr),
          this.fetchModelOfTheDay(dateStr)
        ]);
      } catch (error) {
        this.handleError(error, 'fetchDailySalesAndModel');
      }
    },
    handleError(error, context) {
      console.error(`Error in ${context}:`, error?.message || error);
      if (error.response) {
        this.error = `Error in ${context}: ${error.response.data.message || 'Failed to load data'}`;
      } else if (error.request) {
        this.error = `Network error in ${context}: Could not reach the server.`;
      } else {
        this.error = `Error in ${context}: ${error.message || 'Something broke!'}`;
      }
    },
    async fetchPeriods() {
      this.loadingPeriods = true;
      try {
        const response = await secureAxios.get('/api/dashboard/periods');
        this.periodOptions = response.data.map(period => ({
          value: period.id,
          text: period.name,
          start_date: period.start_date,
          payment_date: period.payment_date
        })) || [];

        this.periodOptions.sort((a, b) => new Date(a.payment_date) - new Date(b.payment_date));

        // Use local date strings to avoid timezone surprises.
        const now = new Date();
        const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

        // Rule: keep showing the current period on its payment_date,
        // and only roll over to the next period on payment_date + 1 day.
        const currentPeriod = this.periodOptions.find(period => {
          const paymentStr = (typeof period.payment_date === 'string')
            ? period.payment_date.split('T')[0]
            : new Date(period.payment_date).toISOString().split('T')[0];

          const cutoff = new Date(`${paymentStr}T00:00:00`); // local midnight
          cutoff.setDate(cutoff.getDate() + 1); // payment_date + 1
          const cutoffStr = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, '0')}-${String(cutoff.getDate()).padStart(2, '0')}`;

          return todayStr < cutoffStr;
        }) || this.periodOptions[this.periodOptions.length - 1];

        this.selectedPeriod = currentPeriod ? currentPeriod.value : null;

        if (this.selectedPeriod) {
          await this.fetchPeriodSales();
          await this.fetchModelOfThePeriod();
        } else {
          if (process.env.NODE_ENV !== 'production') console.warn('No valid period selected.');
        }
      } catch (error) {
        this.handleError(error, 'fetchPeriods');
        this.periodOptions = [{ value: null, text: 'Error Loading Periods' }];
        this.selectedPeriod = null;
      } finally {
        this.loadingPeriods = false;
      }
    },
    async fetchModels() {
      try {
        // Only pass location/status for filtering, no studio_id assumed or passed.
        const params = {};

        // Satellite monitors should always view Satellite across all studios
        if (this.isSatelliteMonitor) {
          params.location = 'satellite';
        } else {
          if (this.activeTab !== 'studio') {
            params.location = this.activeTab;
          }
          if (this.activeTab === 'active') {
            params.status = 'Active';
          }
        }
        // Always use secureAxios; backend applies studio filter via token.
        const response = await secureAxios.get('/api/dashboard/models', { params });
        this.models = response.data || [];
      } catch (error) {
        this.handleError(error, 'fetchModels');
        this.models = [];
      }
    },
    async fetchModelOfTheDay(dateStr) {
      if (!dateStr) return;
      try {
        const params = {
          date: dateStr,
          location: this.activeTab === 'studio' ? null : this.activeTab,
          locations: this.activeTab === 'studio' ? 'morning,evening,night' : null,
          sites: this.sites.map(s => s.key)
        };
        if (this.selectedModel) {
          params.model_id = this.selectedModel.id;
        }
        if (this.activeTab === 'active') {
          params.status = 'Active';
        }
        const response = await secureAxios.get('/api/dashboard/top-model-day', { params });
        this.modelOfTheDay = response.data || null;
      } catch (error) {
        this.handleError(error, 'fetchModelOfTheDay');
        this.modelOfTheDay = null;
      }
    },
    async fetchTopModelRange() {
      if (!this.dateSelectRange || this.dateSelectRange.length < 2) return;
      const dateArray = [...this.dateSelectRange]
        .map(d => new Date(d))
        .sort((a, b) => a - b);
      const startDate = this.formatDate(dateArray[0]);
      const endDate = this.formatDate(dateArray[dateArray.length - 1]);
      try {
        const params = {
          startDate,
          endDate,
          location: this.activeTab === 'studio' ? null : this.activeTab,
          locations: this.activeTab === 'studio' ? 'morning,evening,night' : null,
          sites: this.sites.map(s => s.key)
        };
        if (this.selectedModel) {
          params.model_id = this.selectedModel.id;
        }
        if (this.activeTab === 'active') {
          params.status = 'Active';
        }
        const response = await secureAxios.get('/api/dashboard/top-model-range', { params });
        this.topModelRange = response.data || null;
      } catch (error) {
        this.handleError(error, 'fetchTopModelRange');
        this.topModelRange = null;
      }
    },
    async fetchModelOfThePeriod() {
      if (!this.selectedPeriod) return;
      try {
        const params = {
          periodId: this.selectedPeriod,
          location: this.activeTab === 'studio' ? null : this.activeTab,
          locations: this.activeTab === 'studio' ? 'morning,evening,night' : null,
          sites: this.sites.map(s => s.key)
        };
        if (this.selectedModel) {
          params.model_id = this.selectedModel.id;
        }
        if (this.activeTab === 'active') {
          params.status = 'Active';
        }
        const response = await secureAxios.get('/api/dashboard/top-model-period', { params });
        this.modelOfThePeriod = response.data || null;
      } catch (error) {
        this.handleError(error, 'fetchModelOfThePeriod');
        this.modelOfThePeriod = null;
      }
    },
    async fetchTopSite() {
      try {
        const params = {
          location: this.activeTab === 'studio' ? null : this.activeTab,
          locations: this.activeTab === 'studio' ? 'morning,evening,night' : null,
          sites: this.sites.map(s => s.key)
        };
        if (this.selectedModel) {
          params.model_id = this.selectedModel.id;
        }
        if (this.activeTab === 'active') {
          params.status = 'Active';
        }

        let endpoint;
        if (this.activeCard === 'dailySales' && this.dailySalesDate) {
          const dateStr = this.formatDate(this.dailySalesDate);
          endpoint = `/api/dashboard/top-site-day/${dateStr}`;
        } else if (this.activeCard === 'dateRange' && this.dateSelectRange.length >= 2) {
          const dateArray = [...this.dateSelectRange].map(d => new Date(d)).sort((a, b) => a - b);
          const startDate = this.formatDate(dateArray[0]);
          const endDate = this.formatDate(dateArray[dateArray.length - 1]);
          endpoint = `/api/dashboard/top-site-range/${startDate}/${endDate}`;
        } else if (this.activeCard === 'selectPeriod' && this.selectedPeriod) {
          endpoint = `/api/dashboard/top-site-period/${this.selectedPeriod}`;
        } else {
          return;
        }

        const response = await secureAxios.get(endpoint, { params });
        this.topSite = response.data || null;
      } catch (error) {
        this.handleError(error, 'fetchTopSite');
        this.topSite = null;
      }
    },
    formatDate(date) {
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        const localYesterday = new Date();
        localYesterday.setDate(localYesterday.getDate() - 1);
        return localYesterday.toISOString().split('T')[0];
      }
      return date.toISOString().split('T')[0];
    },
    updateChartData(salesData, dailyBreakdown = null) {
      this.platformSales = {
        flirt4free: Number(salesData.flirt4free) || 0,
        streamate: Number(salesData.streamate) || 0,
        livejasmin: Number(salesData.livejasmin) || 0,
        chaturbate: Number(salesData.chaturbate) || 0,
        stripchat: Number(salesData.stripchat) || 0,
        kwiky: Number(salesData.kwiky) || 0,
        sinparty: Number(salesData.sinparty) || 0,
        cams: Number(salesData.cams) || 0,
        cam4: Number(salesData.cam4) || 0,
        bonga: Number(salesData.bonga) || 0,
        xlove: Number(salesData.xlove) || 0
      };
      this.totalSales = Number(salesData.total) || 0;

      // Only update chartDailySales if dailyBreakdown is provided (i.e., not for Daily Sales card)
      if (dailyBreakdown) {
        this.chartDailySales = dailyBreakdown.map(row => ({
          date: row.date,
          total: Number(row.total) || 0
        }));
      }
    },
    async fetchDailySales(dateStr) {
      if (!dateStr) {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        dateStr = this.formatDate(yesterday);
        this.dailySalesDate = yesterday;
        if (process.env.NODE_ENV !== 'production') console.warn('dateStr was null, set to yesterday:', dateStr);
      }
      try {
        const params = {
          location: this.activeTab === 'studio' ? null : this.activeTab,
          locations: this.activeTab === 'studio' ? 'morning,evening,night' : null,
          sites: this.sites.map(s => s.key)
        };
        if (this.selectedModel) {
          params.model_id = this.selectedModel.id;
        }
        if (this.activeTab === 'active') {
          params.status = 'Active';
        }
        const response = await secureAxios.get(`/api/dashboard/daily-sales/${dateStr}`, { params });

        const breakdown = {
          flirt4free: Number(response.data.flirt4free) || 0,
          streamate: Number(response.data.streamate) || 0,
          livejasmin: Number(response.data.livejasmin) || 0,
          chaturbate: Number(response.data.chaturbate) || 0,
          stripchat: Number(response.data.stripchat) || 0,
          kwiky: Number(response.data.kwiky) || 0,
          sinparty: Number(response.data.sinparty) || 0,
          cams: Number(response.data.cams) || 0,
          cam4: Number(response.data.cam4) || 0,
          bonga: Number(response.data.bonga) || 0,
          xlove: Number(response.data.xlove) || 0
        };

        const total = this.sites.reduce((sum, site) => {
          return site.enabled || Number(breakdown[site.key] || 0) !== 0 ? sum + (breakdown[site.key] || 0) : sum;
        }, 0);

        this.dailySalesBreakdown = { ...breakdown, total };
        this.dailySales = total;

        // Update chart data for the donut chart
        this.updateChartData(this.dailySalesBreakdown);
      } catch (error) {
        this.handleError(error, 'fetchDailySales');
        this.dailySales = 0;
        this.dailySalesBreakdown = {
          flirt4free: 0,
          streamate: 0,
          livejasmin: 0,
          chaturbate: 0,
          stripchat: 0,
          kwiky: 0,
          sinparty: 0,
          cams: 0,
          cam4: 0,
          bonga: 0,
          xlove: 0,
          total: 0
        };
        this.updateChartData(this.dailySalesBreakdown);
      }
    },
    async fetchRangeSales(startDateParam, endDateParam) {
      let startDate, endDate;
      if (startDateParam && endDateParam) {
        startDate = startDateParam;
        endDate = endDateParam;
      } else {
        if (!this.dateSelectRange || this.dateSelectRange.length < 2) {
          if (process.env.NODE_ENV !== 'production') console.warn('Invalid date range');
          return;
        }
        const dateArray = [...this.dateSelectRange]
          .map(d => new Date(d))
          .sort((a, b) => a - b);
        startDate = this.formatDate(dateArray[0]);
        endDate = this.formatDate(dateArray[dateArray.length - 1]);
      }
      try {
        const params = {
          location: this.activeTab === 'studio' ? null : this.activeTab,
          locations: this.activeTab === 'studio' ? 'morning,evening,night' : null,
          sites: this.sites.map(s => s.key)
        };
        if (this.selectedModel) {
          params.model_id = this.selectedModel.id;
        }
        if (this.activeTab === 'active') {
          params.status = 'Active';
        }
        const totalResponse = await secureAxios.get(`/api/dashboard/range-sales/${startDate}/${endDate}`, { params });
        const breakdownResponse = await secureAxios.get(`/api/dashboard/range-sales-breakdown/${startDate}/${endDate}`, { params });

        this.rangeSales = this.sites.reduce((sum, site) => {
          const value = Number(totalResponse.data[site.key]);
          if (site.enabled || (!isNaN(value) && value !== 0)) {
            return sum + (isNaN(value) ? 0 : value);
          }
          return sum;
        }, 0);
        this.rangeSalesBreakdown = {
          flirt4free: Number(totalResponse.data.flirt4free) || 0,
          streamate: Number(totalResponse.data.streamate) || 0,
          livejasmin: Number(totalResponse.data.livejasmin) || 0,
          chaturbate: Number(totalResponse.data.chaturbate) || 0,
          stripchat: Number(totalResponse.data.stripchat) || 0,
          kwiky: Number(totalResponse.data.kwiky) || 0,
          sinparty: Number(totalResponse.data.sinparty) || 0,
          cams: Number(totalResponse.data.cams) || 0,
          cam4: Number(totalResponse.data.cam4) || 0,
          bonga: Number(totalResponse.data.bonga) || 0,
          xlove: Number(totalResponse.data.xlove) || 0
        };

        // Only update chartDailySales for the range breakdown if activeCard === 'dateRange'
        if (this.activeCard === 'dateRange') {
          this.chartDailySales = breakdownResponse.data.map(row => ({
            date: row.date,
            total: Number(row.total) || 0
          }));
        }
      } catch (error) {
        this.handleError(error, 'fetchRangeSales');
        this.rangeSales = 0;
        this.rangeSalesBreakdown = {
          flirt4free: 0,
          streamate: 0,
          livejasmin: 0,
          chaturbate: 0,
          stripchat: 0,
          kwiky: 0,
          sinparty: 0,
          cams: 0,
          cam4: 0,
          bonga: 0,
          xlove: 0
        };
        this.chartDailySales = [];
      }
    },
    async fetchPeriodSales() {
      if (!this.selectedPeriod) {
        if (process.env.NODE_ENV !== 'production') console.warn('No period selected');
        return;
      }
      try {
        const params = {
          location: this.activeTab === 'studio' ? null : this.activeTab,
          locations: this.activeTab === 'studio' ? 'morning,evening,night' : null,
          sites: this.sites.map(s => s.key)
        };
        if (this.selectedModel) {
          params.model_id = this.selectedModel.id;
        }
        if (this.activeTab === 'active') {
          params.status = 'Active';
        }
        // Keep totalResponse fetch for periodSales card display, but do NOT update this.totalSales or this.platformSales here
        const totalResponse = await secureAxios.get(`/api/dashboard/period-sales/${this.selectedPeriod}`, { params });
        const breakdownResponse = await secureAxios.get(`/api/dashboard/period-sales-breakdown/${this.selectedPeriod}`, { params });

        // Set periodSales for the card display
        this.periodSales = this.sites.reduce((sum, site) => {
          const value = Number(totalResponse.data[site.key]);
          if (site.enabled || (!isNaN(value) && value !== 0)) {
            return sum + (isNaN(value) ? 0 : value);
          }
          return sum;
        }, 0);

        this.periodSalesBreakdown = {
          flirt4free: Number(totalResponse.data.flirt4free) || 0,
          streamate: Number(totalResponse.data.streamate) || 0,
          livejasmin: Number(totalResponse.data.livejasmin) || 0,
          chaturbate: Number(totalResponse.data.chaturbate) || 0,
          stripchat: Number(totalResponse.data.stripchat) || 0,
          kwiky: Number(totalResponse.data.kwiky) || 0,
          sinparty: Number(totalResponse.data.sinparty) || 0,
          cams: Number(totalResponse.data.cams) || 0,
          cam4: Number(totalResponse.data.cam4) || 0,
          bonga: Number(totalResponse.data.bonga) || 0,
          xlove: Number(totalResponse.data.xlove) || 0
        };

        // Only update chartDailySales if activeCard === 'selectPeriod'
        if (this.activeCard === 'selectPeriod') {
          this.chartDailySales = breakdownResponse.data.map(row => ({
            date: row.date,
            total: Number(row.total) || 0
          }));
        }
      } catch (error) {
        this.handleError(error, 'fetchPeriodSales');
        this.periodSalesBreakdown = {
          flirt4free: 0,
          streamate: 0,
          livejasmin: 0,
          chaturbate: 0,
          stripchat: 0,
          kwiky: 0,
          sinparty: 0,
          cams: 0,
          cam4: 0,
          bonga: 0,
          xlove: 0
        };
        this.periodSales = 0;
        this.chartDailySales = [];
      }
    },
    setActiveCard(card) {
      if (card === 'viewSites') return;

      this.activeCard = card;

      if (card === 'dateRange' && this.rangeSalesBreakdown && this.dateSelectRange.length >= 2) {
        const dateArray = [...this.dateSelectRange].map(d => new Date(d)).sort((a, b) => a - b);
        const startDate = this.formatDate(dateArray[0]);
        const endDate = this.formatDate(dateArray[dateArray.length - 1]);
        this.fetchRangeSales(startDate, endDate);
      } else if (card === 'selectPeriod' && this.periodSalesBreakdown && this.selectedPeriod) {
        this.fetchPeriodSales();
      }
    },
    handleDateRangeSelection(newRange) {
      if (newRange.length === 2) {
        this.$nextTick(() => {
          if (this.$refs.datePicker) {
            this.$refs.datePicker.isActive = false;
          }
        });
      }
    }
  }
};
</script>

<style scoped>
.dashboard-tab .v-tab {
  background-color: white;
  color: #2C74B3;
}
.active-card {
  border: 2px solid #1976d2 !important;
}
</style>
